/**
 * Read an image paint from PDF.js's decoded page resources.
 * PDF.js may expose pixels (1 bit grey / RGB / RGBA), or an ImageBitmap;
 * shared XObjects may live in commonObjs rather than objs.
 * Do not treat a screenshot of a whole PDF layer as an extracted image.
 */

const MAX_PIXELS = 30_000_000;

/** Convert PDF.js ImageData into browser-compatible RGBA pixels. */
export function toRgbaPixels(image) {
  const width = image?.width, height = image?.height;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width*height > MAX_PIXELS) return null;
  const data = image.data;
  if (!data || typeof data.length !== 'number') return null;
  const count = width * height;
  const rgba = new Uint8ClampedArray(count*4);
  // The PDF.js public ImageKind values are 1: grey 1-bit, 2: RGB, 3: RGBA.
  // Check kind before length: a small RGB bitmap can coincidentally have the
  // same byte count as a packed mask under unusual dimensions.
  if (image.kind === 1) {
    const stride = Math.ceil(width / 8);
    if (data.length < stride * height) return null;
    for(let y=0;y<height;y++) for(let x=0;x<width;x++) {
      const byte = data[y*stride + (x>>3)];
      const grey = (byte & (0x80 >> (x&7))) ? 255 : 0;
      const k = (y*width+x)*4;
      rgba[k]=rgba[k+1]=rgba[k+2]=grey;
      rgba[k+3]=255;
    }
    return { width, height, data:rgba };
  }
  if (image.kind === 3 || data.length === count*4) {
    if (data.length < count*4) return null;
    rgba.set(data.subarray ? data.subarray(0,count*4) : Array.from(data).slice(0,count*4));
  } else if (image.kind === 2 || data.length === count*3) {
    if (data.length < count*3) return null;
    for(let i=0;i<count;i++) {
      rgba[4*i]=data[3*i]; rgba[4*i+1]=data[3*i+1]; rgba[4*i+2]=data[3*i+2]; rgba[4*i+3]=255;
    }
  } else return null;
  return { width,height,data:rgba };
}

/** Resolve objects in both page caches (shared resources are often prefixed g_). */
export function findPaintResource(page, imageId) {
  if (typeof imageId !== 'string') return null;
  const caches = imageId.startsWith('g_')
    ? [page.commonObjs, page.objs] : [page.objs, page.commonObjs];
  for (const store of caches) {
    if (!store) continue;
    try {
      // PDFObjects.get throws when a resource hasn't been resolved yet.
      // A completed page.render generally fills the PDF.js object cache.
      if (typeof store.has === 'function' && !store.has(imageId)) continue;
      const found = store.get(imageId);
      if (found) return found;
    } catch { /* try another cache */ }
  }
  return null;
}

/** Some XObjects are resolved asynchronously after getOperatorList().
 * The callback form of PDFObjects.get waits for the worker to deliver them.
 * Cap the wait so a missing ID never freezes the selection UI.
 */
export async function resolvePaintResource(page, imageId, timeoutMs=450) {
  const cached=findPaintResource(page,imageId);
  if(cached) return cached;
  if(typeof imageId!=='string') return null;
  const stores=imageId.startsWith('g_')?[page.commonObjs,page.objs]:[page.objs,page.commonObjs];
  const requests=stores.filter(store=>store && typeof store.get==='function').map(store=>
    new Promise(resolve=>{
      try { store.get(imageId,data=>resolve(data||null)); }
      catch { resolve(null); }
    })
  );
  if(!requests.length) return null;
  let timer;
  try {
    return await Promise.race([
      new Promise(resolve=>{
        let remaining=requests.length;
        for(const promise of requests) promise.then(value=>{
          if(value) resolve(value);
          else if(!--remaining) resolve(null);
        });
      }),
      new Promise(resolve=>{timer=setTimeout(()=>resolve(null),timeoutMs);}),
    ]);
  } finally { clearTimeout(timer); }
}

function canvasToBlob(canvas) {
  return new Promise(resolve => canvas.toBlob(blob => resolve(blob || null), 'image/png'));
}

export async function imageObjectToBlob(image, createCanvas = () => document.createElement('canvas')) {
  if (!image) return null;
  const width=image.width, height=image.height;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width*height > MAX_PIXELS) return null;
  const canvas=createCanvas();
  canvas.width=width;canvas.height=height;
  const ctx=canvas.getContext('2d');
  if (!ctx) return null;
  // Newer PDF.js workers transfer decoded image data in ImageBitmap instead
  // of keeping a Uint8Array. `data` can be null in that case.
  if (image.bitmap) {
    ctx.drawImage(image.bitmap,0,0,width,height);
    return canvasToBlob(canvas);
  }
  // Some PDF.js operators contain an image/canvas drawable directly.
  if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
    ctx.drawImage(image,0,0,width,height);
    return canvasToBlob(canvas);
  }
  const pixels=toRgbaPixels(image);
  if (!pixels) return null;
  ctx.putImageData(new ImageData(pixels.data,width,height),0,0);
  return canvasToBlob(canvas);
}

export async function extractPaintedPdfImage(page,operatorIndex,OPS, options={}) {
  const operatorList=await page.getOperatorList();
  const op=operatorList.fnArray?.[operatorIndex];
  const args=operatorList.argsArray?.[operatorIndex]||[];
  let image=null;
  if (op===OPS.paintInlineImageXObject) image=args[0];
  else if (op===OPS.paintImageXObject || op===OPS.paintJpegXObject) {
    image=await resolvePaintResource(page,args[0]);
  }
  // Other operations, like masks/groups/repeated image paints, are composite
  // graphics and must not be mistaken for a self-contained bitmap.
  return imageObjectToBlob(image,options.createCanvas);
}
